import type { MaybeRef } from '@vueuse/core';
import { get } from '@vueuse/core';
import orderBy from 'lodash/orderBy';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import type { IOutright } from '@/interface/IOutright';
import { SportType } from '@/interface/enum';
import { deepCompareComputed } from '@/libraries/composables/deepCompareComputed';
import BusinessLogicHelper from '@/libraries/helpers/businessLogicHelper';
import { getTranslated } from '@/libraries/helpers/translationHelper';
import { formatOutright } from '@/libraries/oddsApi/oddsApiFormatter';
import { useOddsApiStore } from '@/stores/oddsApiStore';
import { useRootStore } from '@/stores/rootStore';
import { useOutrightsQuery, useOutrightsSubscription } from '@/types';

type OddsQueryVariable = {
    leagueId: MaybeRef<number>;
    sportType: MaybeRef<SportType>;
    isEnabled: MaybeRef<boolean>;
    isLive?: MaybeRef<boolean>;
}

export function useOutrights(
    { isLive, isEnabled, sportType, leagueId }: OddsQueryVariable,
) {
    const outrights = ref<IOutright[]>([]);

    const oddsApiSportType = SportType.toOddsApi(get(sportType));
    const variable = deepCompareComputed(() => ({
        query: {
            sport: oddsApiSportType,
            tournamentId: get(leagueId),
            isLive: get(isLive),
        },
    }));

    const { isWsConnected } = storeToRefs(useRootStore());

    function getPollInterval() {
        if (isWsConnected.value) {
            return undefined;
        }
        return 60000;
    }

    const { onResult, refetch, loaded } = useOutrightsQuery(
        variable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled),
            pollInterval: getPollInterval(),
        })),
    );

    const { customerSettings } = storeToRefs(useOddsApiStore());

    onResult((data) => {
        if (!data) {
            return;
        }

        const filtered = data
            .map(outright => ({
                ...outright,
                tournament: {
                    sport: {
                        sportType: oddsApiSportType,
                    },
                },
            }))
            .filter(outright => BusinessLogicHelper.isOutrightAllowed(customerSettings.value, outright))
            .map(e => Object.freeze(formatOutright(e)));

        outrights.value = filtered;
    });

    const { onSubscription } = useOutrightsSubscription(
        variable,
        deepCompareComputed(() => ({
            enabled: get(isEnabled),
        })),
    );

    onSubscription((data) => {
        if (!data) {
            return;
        }

        const oldData = [...outrights.value];
        const { updated, deleted } = data;
        updated
            .map(outright => ({
                ...outright,
                tournament: {
                    sport: {
                        sportType: oddsApiSportType,
                    },
                },
            }))
            .filter(outright => BusinessLogicHelper.isOutrightAllowed(customerSettings.value, outright))
            .map(e => Object.freeze(formatOutright(e)))
            .forEach((newOdds) => {
                const oldOddsIndex = oldData.findIndex(
                    oldOdds => newOdds.id === oldOdds.id,
                );
                if (oldOddsIndex !== -1) {
                    oldData[oldOddsIndex] = newOdds;
                } else {
                    oldData.push(newOdds);
                }
            });

        outrights.value = oldData.filter(oldOdds => !deleted.includes(oldOdds.id));
    });

    const orderedOutrights = computed(() => orderBy(outrights.value, [x => x.price, (x) => {
        const teamName = getTranslated(x.teamName);
        return teamName.toUpperCase();
    }]));

    return {
        loaded,
        outrights: orderedOutrights,
        refetch,
    };
}
